import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type { ConnectionCancellationPolicies } from '@luxuryescapes/lib-refunds'
import { formatDate } from '@luxuryescapes/lib-refunds'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import TextLink from 'LuxKit/TextLink'
import { Refundable, getRefundDescriptions } from '../utils/getCancellationPolicyStatus'
import { getConnectionRefundPolicyDetails } from '../utils/getAlwaysOnConnectionRefundPolicyDetails'
import ConnectionCancellationPolicyModal from './ConnectionCancellationPolicyModal'
import ModalContext from 'contexts/ModalContext'
import useNonAmendableWording from '../useNonAmendableWording'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicies: ConnectionCancellationPolicies
  cancellationPolicy?: App.OfferCancellationPolicy
  checkIn?: string
  className?: string
  hideNonRefundableMessage?: boolean
  textLinkFormat?: boolean
  hideExcludesFlights?: boolean
  offerType?: string
  showHotelOnly?: boolean
  enableNonAmendableWording?: boolean
}

function ConnectionCancellationPoliciesComponent(props: Props) {
  const { cancellationPolicies, cancellationPolicy, className, checkIn, hideNonRefundableMessage, textLinkFormat, hideExcludesFlights, offerType, enableNonAmendableWording = false, showHotelOnly = false } = props
  const intl = useIntl()
  const { deadline, partiallyRefundableUntil, refundable, timezone } = getConnectionRefundPolicyDetails(cancellationPolicies, checkIn)
  const isRental = offerType === 'rental'
  const refundableIcon = isRental ? <SolidShieldCheckIcon /> : <LineRefundIcon />
  const {
    nonAmendableDetail,
    nonAmendableLabel,
    showNonAmendableWording,
  } = useNonAmendableWording(enableNonAmendableWording && !hideNonRefundableMessage && refundable === Refundable.Non)

  const refundDescriptions = useMemo(() => getRefundDescriptions(offerType, intl), [offerType, intl])

  const partiallyRefundableLabel = intl.formatMessage({
    id: 'KIcFv84yrcW6EUU6GMiE',
    defaultMessage: 'Partially refundable',
  })

  const freeCancellationAvailableLabel = showHotelOnly ? intl.formatMessage({
    id: 'RsaNQC6EEN_gQvW1jrRm',
    defaultMessage: 'Free cancellation available (Hotel only)',
  }) : intl.formatMessage({
    id: 'Ih80cS-Z5Yp1RT31VoFD',
    defaultMessage: 'Free cancellation available',
  })

  const untilDeadlineHotelOnlyLabel = deadline ? intl.formatMessage(
    {
      id: 'wfLIxj6fYkQqGtxBYAX2',
      defaultMessage: 'until {date} (Hotel only)',
    },
    { date: formatDate.formattedShortDateByTimezone(deadline, timezone) },
  ) : undefined

  const untilDeadlineLabel = deadline ? intl.formatMessage(
    {
      id: 'GIp4r3X1S8zCUzA7hADJ',
      defaultMessage: 'until {date}',
    },
    { date: formatDate.formattedShortDateByTimezone(deadline, timezone) },
  ) : undefined

  const untilPartialDeadlineLabel = partiallyRefundableUntil ? intl.formatMessage(
    {
      id: 'v3sVSGm-j_ahK-lom0Ca',
      defaultMessage: 'until {date}',
    },
    { date: formatDate.formattedShortDateByTimezone(partiallyRefundableUntil, timezone) },
  ) : undefined

  const untilPartialDeadlineHotelOnlyLabel = partiallyRefundableUntil ? intl.formatMessage(
    {
      id: 'wfLIxj6fYkQqGtxBYAX2',
      defaultMessage: 'until {date} (Hotel only)',
    },
    { date: formatDate.formattedShortDateByTimezone(partiallyRefundableUntil, timezone) },
  ) : undefined

  const refundLabel = showHotelOnly ? intl.formatMessage({
    id: 'W0-uhD-9El1C6szCbkA7',
    defaultMessage: '{refundType} (Hotel only)',
  }, { refundType: refundDescriptions[refundable] }) : refundDescriptions[refundable]

  const showModal = useContext(ModalContext)

  const onClickIcon = useCallback(() => {
    showModal(<ConnectionCancellationPolicyModal
      title={showNonAmendableWording ? nonAmendableLabel : refundDescriptions[refundable]}
      hideExcludesFlights={!!hideExcludesFlights}
      cancellationPolicyLines={showNonAmendableWording ? [...cancellationPolicies.description, nonAmendableDetail] : cancellationPolicies.description}
      cancellationPolicyType={cancellationPolicy?.type}
    />)
  }, [showModal, refundable, refundDescriptions, hideExcludesFlights, cancellationPolicies, cancellationPolicy, showNonAmendableWording, nonAmendableLabel, nonAmendableDetail])

  return (
    <Clickable onClick={onClickIcon} className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {!hideNonRefundableMessage && (
        <>
          {refundable === Refundable.Fully && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-primary-dark"
                  underline={false}
                  startIcon={refundableIcon}
                >
                  {refundLabel}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">{refundDescriptions[refundable]}</BodyText>
                  {deadline &&
                    <BodyText variant="medium" as="span">{showHotelOnly ? untilDeadlineHotelOnlyLabel : untilDeadlineLabel}</BodyText>}
                  <MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {refundable === Refundable.Partial && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-primary-dark"
                  underline={false}
                  startIcon={refundableIcon}
                >
                  {refundLabel}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">{partiallyRefundableLabel}</BodyText>
                  {partiallyRefundableUntil &&
                    <BodyText variant="medium" as="span">{showHotelOnly ? untilPartialDeadlineHotelOnlyLabel : untilPartialDeadlineLabel}</BodyText>}
                  <MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {refundable === Refundable.Non && (
            <>
              <BodyText variant="medium" as="span">{showNonAmendableWording ? nonAmendableLabel : refundDescriptions[refundable]}</BodyText>
              <MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
      {hideNonRefundableMessage && refundable === Refundable.Fully && (
        <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-primary-dark"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {freeCancellationAvailableLabel}
            </TextLink>}
          {!textLinkFormat && (
            <>
              <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">{freeCancellationAvailableLabel}</BodyText>
              {deadline &&
                <BodyText variant="medium" as="span"> {untilDeadlineLabel}</BodyText>}
              <MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
    </Clickable>
  )
}

export default ConnectionCancellationPoliciesComponent
